import { parseActionError } from './adminUi.js'

export const NOTIFICATION_STATUS_META = {
  queued: { label: 'Queued', tone: 'warning' },
  sent: { label: 'Sent', tone: 'active' },
  delivered: { label: 'Delivered', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  bounced: { label: 'Bounced', tone: 'danger' },
  retrying: { label: 'Retrying', tone: 'warning' },
}

const KNOWN_NOTIFICATION_STATUSES = new Set(Object.keys(NOTIFICATION_STATUS_META))

export const normalizeNotificationStatus = (statusOrNotification) => {
  const candidate = typeof statusOrNotification === 'string'
    ? statusOrNotification
    : statusOrNotification?.delivery_status ?? statusOrNotification?.status ?? statusOrNotification?.state ?? ''
  const normalized = String(candidate || '').trim().toLowerCase().replace(/\s+/g, '_')
  return KNOWN_NOTIFICATION_STATUSES.has(normalized) ? normalized : 'queued'
}

export const getNotificationStatusMeta = (statusOrNotification) => {
  const status = normalizeNotificationStatus(statusOrNotification)
  return NOTIFICATION_STATUS_META[status] || NOTIFICATION_STATUS_META.queued
}

export const canRetryNotification = (statusOrNotification) => {
  const status = normalizeNotificationStatus(statusOrNotification)
  return status === 'failed' || status === 'bounced'
}

export const toNotificationUserMessage = (statusOrNotification) => {
  const status = normalizeNotificationStatus(statusOrNotification)
  if (status === 'delivered') return 'Notification delivered successfully.'
  if (status === 'sent') return 'Notification was sent and is awaiting delivery confirmation.'
  if (status === 'failed') return 'Notification delivery failed and can be retried.'
  if (status === 'bounced') return 'Notification was rejected by the destination.'
  if (status === 'retrying') return 'Notification delivery is being retried.'
  return 'Notification is queued for delivery.'
}

export const toNotificationDegradedMessage = (notification) => {
  if (notification?.degraded === true || notification?.partial === true) {
    return 'Delivery status may be incomplete while retries or provider sync are still running.'
  }
  return ''
}

export const normalizeNotificationPayload = (payload, index = 0) => {
  const source = payload && typeof payload === 'object' ? payload : {}
  return {
    id: source?.id ?? source?.notification_id ?? `notification-${index}`,
    channel: source?.channel ?? source?.delivery_channel ?? 'unknown',
    recipient: source?.recipient ?? source?.masked_recipient ?? 'unknown',
    status: normalizeNotificationStatus(source),
    subject: source?.subject ?? source?.template_name ?? 'Notification',
    correlationRef: source?.correlation_ref ?? source?.correlation_reference ?? '',
    createdAt: source?.created_at ?? source?.queued_at ?? null,
    updatedAt: source?.updated_at ?? source?.sent_at ?? null,
    degraded: source?.degraded === true,
    partial: source?.partial === true,
    raw: source,
  }
}

export const parseNotificationError = (err, fallback = 'Notification operation failed.') => {
  const detail = String(err?.response?.data?.detail || err?.message || '').toLowerCase()
  if (detail.includes('already retrying') || detail.includes('duplicate retry')) {
    return 'This notification is already being retried. Refresh to see the latest status.'
  }
  return parseActionError(err, fallback, {
    conflict: 'Notification state changed on the server. Refresh and retry if needed.',
    forbidden: 'You do not have permission to manage this notification.',
    notFound: 'Notification could not be found. Refresh and try again.',
  })
}

